// src/components/LoginPage.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import api from '../api/axios';
import { shouldUseMockAuth, mockLogin } from '../utils/mockAuth';
import { getCurrentUser } from '../services/users';
import { useUser } from '../contexts/UserContext';

function LoginPage({ onLoginSuccess }) {
  const navigate = useNavigate();
  const useMock = shouldUseMockAuth();
  const { setUser } = useUser();

  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      let response;

      // Se il mock è attivo, usa il mock direttamente
      if (useMock) {
        response = await mockLogin(username, password);
      } else {
        // Altrimenti prova con il backend reale
        response = await api.post('api/token/', {
          username: username,
          password: password,
        });
      }

      // Salva i token JWT in localStorage
      const { access, refresh } = response.data;
      localStorage.setItem('accessToken', access);
      localStorage.setItem('refreshToken', refresh);

      // Salva preferenza "ricordami" se selezionata
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      }

      // Fetch dei dati utente e sincronizzazione con UserContext
      // Questo previene la race condition tra salvataggio token e redirect
      try {
        const userResponse = await getCurrentUser();
        // Aggiorna UserContext con i dati utente (incluso enabled_modules)
        setUser(userResponse.data);
        if (onLoginSuccess) {
          onLoginSuccess(userResponse.data);
        }
      } catch (userErr) {
        // Se il fetch utente fallisce, procedi comunque (mock mode o backend issues)
        console.warn('Could not fetch user after login:', userErr);
        if (onLoginSuccess) {
          // Passa dati minimi per evitare problemi di stato
          onLoginSuccess({ username });
        }
      }

      // Naviga alla nuova dashboard in caso di successo
      navigate('/dashboard-new');
    } catch (err) {
      console.error('Errore di login:', err);
      setError('Credenziali non valide. Riprova.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8 relative bg-gradient-to-b from-[#f8f5ff] via-[#f3edff] to-[#ede5ff]">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(180,140,255,0.25),transparent_70%)] pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-2xl p-8 space-y-6 shadow-[0_12px_30px_rgba(80,60,150,0.25),0_25px_60px_rgba(120,90,200,0.35),0_0_30px_rgba(160,120,255,0.30)] border border-[#EFE5FF] relative">
          {/* Card overlay */}
          <div className="absolute inset-0 bg-[rgba(250,245,255,0.55)] rounded-2xl pointer-events-none" />

          <div className="relative z-10 space-y-6">
            <h2 className="text-2xl font-semibold text-center text-[#2E2A3B]">Login</h2>

            {/* Mock Auth Alert */}
            {useMock && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
                <strong>Modalità Mock Attiva</strong>
                <p className="mt-1">Il backend non è disponibile. Puoi usare qualsiasi username/password per accedere.</p>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Username Field */}
              <div className="space-y-2">
                <Label htmlFor="username" className="text-[#443B59] text-sm font-medium">
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="bg-white border-[#E0D6FF] focus:border-[#6A4DFF] focus:ring-[#6A4DFF] h-12 px-4 text-[#2E2A3B] placeholder:text-[#B8AFCF] focus:shadow-[0_0_10px_rgba(150,110,255,0.35)] transition-all"
                />
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#443B59] text-sm font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-white border-[#E0D6FF] focus:border-[#6A4DFF] focus:ring-[#6A4DFF] h-12 px-4 pr-12 text-[#2E2A3B] placeholder:text-[#B8AFCF] focus:shadow-[0_0_10px_rgba(150,110,255,0.35)] transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8C70FF] hover:text-[#6A4DFF] transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Remember Me Checkbox */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="border-[#E0D6FF] data-[state=checked]:bg-[#6A4DFF] data-[state=checked]:border-[#6A4DFF]"
                />
                <Label htmlFor="remember" className="text-[#443B59] text-sm font-medium cursor-pointer">
                  Ricordami
                </Label>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-14 rounded-lg bg-gradient-to-r from-[#6A4DFF] to-[#D362FF] text-white font-semibold text-lg shadow-[0_8px_20px_rgba(150,110,255,0.35),0_0_20px_rgba(180,140,255,0.45)] hover:shadow-[0_12px_24px_rgba(150,110,255,0.45),0_0_25px_rgba(180,140,255,0.55)] hover:brightness-110 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {isLoading ? 'Accesso in corso...' : 'Entra'}
              </button>
            </form>

            {/* Divider */}
            <div className="border-t border-[#EFE5FF]" />

            {/* Forgot Password Link */}
            <div className="text-center">
              <a href="#" className="text-[#6A4DFF] hover:text-[#5A3DE0] hover:underline text-sm transition-colors">
                Password dimenticata?
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Logo HEXA */}
      <div className="fixed bottom-6 left-6 flex items-center gap-2 opacity-60">
        <img src="/images/hexa-logo.png" alt="HEXA" className="w-8 h-8 object-contain" />
        <span className="text-[#6A4DFF] text-sm font-medium">HEXA</span>
      </div>
    </div>
  );
}

export default LoginPage;
